import {injectable} from 'inversify'
import {makeObservable} from 'mobx'
import i18next from 'i18next'
import {FilterFormFieldType} from '@/types'
import {getNotEmptyValues} from '@/utils'
import {BaseFilterContainerStore} from '@/stores/shared'
import {StoreMidTerminalFilterStore} from '@/stores/store-and-terminals'
import {DccBackendFilterType, DccFilterType} from '../models'
import {TerminalType} from '~/code/constants'

@injectable()
export class DccFilterStore extends BaseFilterContainerStore<DccFilterType, DccBackendFilterType> {
    private _smtfStore = new StoreMidTerminalFilterStore({useAllData: true})

    constructor(
        private options: {
            hiddenFields?: string[]
        } = {}
    ) {
        super()

        makeObservable(this, {
            ...super.getAnnotationsMap()
        })
    }

    get loadingState() {
        return this._smtfStore.loadingState
    }

    get filter() {
        return {
            ...this._filter,
            store: this._smtfStore.selectedStore,
            mid: this._smtfStore.selectedMid,
            terminalId: this._smtfStore.selectedTerminal
        }
    }

    get fields(): FilterFormFieldType[] {
        const fields: FilterFormFieldType[] = [
            {
                name: 'store',
                label: i18next.t('globals:fields.store'),
                type: 'selectWithTwoLabels',
                isHidden:
                    this._smtfStore.storesAsSelectItemsWithAll.length === 0 ||
                    this._smtfStore.selectedTerminalType !== TerminalType.POS,
                data: {
                    optionsWithTwoLabels: this._smtfStore.storesAsSelectItemsWithAll
                }
            },
            {
                name: 'mid',
                label: i18next.t('globals:fields.mid'),
                type: 'select',
                isHidden: this._smtfStore.midsAsSelectItemWithAll.length === 0,
                data: {
                    options: this._smtfStore.midsAsSelectItemWithAll
                }
            },
            {
                name: 'terminalId',
                label: i18next.t('globals:fields.terminalId'),
                type: 'select',
                isHidden: this._smtfStore.terminalsAsSelectItemWithAll.length === 0,
                data: {
                    options: this._smtfStore.terminalsAsSelectItemWithAll
                }
            }
        ]

        return fields.filter((f) => !this.options.hiddenFields?.includes(f.name))
    }

    isFilterSelected(field: string, value: any): boolean {
        if (field === 'store' && this._smtfStore.storesAsSelectItemsWithAll.length === 1) {
            return false
        }
        return super.isFilterSelected(field, value)
    }

    getInitialFilter() {
        return {}
    }

    setFilter(filter: Partial<DccFilterType>) {
        const {store, mid, terminalId, ...rest} = filter
        super.setFilter(rest)

        if (store !== undefined) {
            this._smtfStore.setStore(store)
        }
        if (mid !== undefined) {
            this._smtfStore.setMid(mid)
        }
        if (terminalId !== undefined) {
            this._smtfStore.setTerminal(terminalId as string)
        }
    }

    clear() {
        this._smtfStore.clear()
        super.clear()
    }

    getFilterForBackend = (): DccBackendFilterType => {
        const {terminalId, ...rest} = this._smtfStore.requestForServer
        return getNotEmptyValues({
            ...rest,
            terminalIds: terminalId
        }) as DccBackendFilterType
    }
}
